import type { PlanChangeItem } from '@api/policy/billing';
import { policyClient } from '@api/sdk-client';
import { useAuth } from '@clerk/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect } from 'react';
import type { ReactNode } from 'react';
import { isClerkAuthEnabled } from 'src/utils/clerkAuth';

export interface ClerkMeAccount {
  id: string;
  name: string;
  market: string;
  role: string;
}

export type ClerkMeSubscriptionStatus =
  | 'INCOMPLETE'
  | 'TRIALING'
  | 'ACTIVE'
  | 'CANCELING'
  | 'PAST_DUE'
  | 'EXPIRED'
  | 'PENDING_CLAIM';

export interface ClerkMeSubscription {
  planKey: string;
  status: ClerkMeSubscriptionStatus;
  interval: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  trialEnd: string | null;
  cancelAt: string | null;
  stripeScheduleId: string | null;
  pendingUpdate: boolean;
  scheduledPlanKey: string | null;
  scheduledInterval: string | null;
  scheduledAt: string | null;
  /** Per-item scheduled boundaries; `scheduledAt` is only the earliest. */
  /** null means Stripe could not confirm the active schedule; [] means none. */
  scheduledChanges: PlanChangeItem[] | null;
}

export interface ClerkMeResponse {
  user: { id: string };
  accounts: ClerkMeAccount[];
  activeAccount: ClerkMeAccount | null;
  subscription: ClerkMeSubscription | null;
  hasStripeCustomer: boolean;
  hasDefaultPaymentMethod: boolean;
}

export interface ClerkEntitlementsResponse {
  accountId: string;
  market: string;
  accessLevel?: 'FULL' | 'READ_ONLY';
  features: Array<{
    id: number;
    name: string;
    domain: string;
    country: string;
    usageLimit: number | null;
    renewal: string | null;
    synupProviders: Array<{
      name: string;
      synupId: number | null;
      isListingProvider: boolean;
      isReviewsProvider: boolean;
    }>;
  }>;
  featureKeys: string[];
  socialBrandQuantity: number;
  listingLocationQuantity: number;
}

export interface ClerkMeData extends ClerkMeResponse {
  entitlements: ClerkEntitlementsResponse | null;
}
interface ClerkMeIdentity {
  isLoaded: boolean;
  isSignedIn: boolean | undefined;
  userId: string | null | undefined;
  orgId: string | null | undefined;
}

const ClerkMeIdentityContext = createContext<ClerkMeIdentity | null>(null);

export function ClerkMeIdentityProvider({ children }: { children: ReactNode }) {
  const identity = useAuth();
  return <ClerkMeIdentityContext.Provider value={identity}>{children}</ClerkMeIdentityContext.Provider>;
}

export function useClerkMe() {
  const identity = useContext(ClerkMeIdentityContext);
  const isClerkMode = isClerkAuthEnabled();
  const isLoaded = identity?.isLoaded ?? false;
  const isSignedIn = identity?.isSignedIn;
  const userId = identity?.userId;
  const orgId = identity?.orgId;
  const queryClient = useQueryClient();
  const identityKey = ['clerkMe', userId ?? null, orgId ?? null] as const;

  useEffect(() => {
    if (!isLoaded || (isSignedIn && !userId)) return;
    void queryClient.removeQueries({
      predicate: (query) =>
        query.queryKey[0] === 'clerkMe' &&
        (userId == null || query.queryKey[1] !== userId || query.queryKey[2] !== orgId),
    });
  }, [isLoaded, isSignedIn, orgId, queryClient, userId]);

  const query = useQuery({
    queryKey: identityKey,
    queryFn: async (): Promise<ClerkMeData> => {
      const { data: me } = await policyClient.instance.get<ClerkMeResponse>('/v1/me');
      const entitlements = me.activeAccount
        ? (await policyClient.instance.get<ClerkEntitlementsResponse>('/v1/me/entitlements')).data
        : null;
      return { ...me, entitlements };
    },
    enabled: isClerkMode && isLoaded && isSignedIn === true && Boolean(userId),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  return { ...query, isReadOnly: query.data?.entitlements?.accessLevel === 'READ_ONLY' };
}
